import React, { useState, Fragment, useEffect, useRef } from 'react'
import cn from 'classnames'

import Icon from '../Icon2/Icon'
import Tooltip from '../Tooltip/Tooltip'
import './SideBarWindow.css'
import Dropdown from '../DropDown/Dropdown'

type subTabsArray = {
  id: string;
  tabLabel?: string;
  tabIcon?: React.ReactNode;
  hasNotification?: Boolean;
  notifTabIcon?: React.ReactNode;
  data?: React.ReactNode;
  windowTitle?: string | React.ReactNode;
  width?: string;
  discussionPanelClassName?: string;
  disableSidebar?: boolean;
  enableTab?: boolean;
  enableInEntryCreatePage?: boolean;
  isIndependentlyActive?: boolean;
  onClick: () => void;
  tabTestId?: string;
  className?: string;
};

type IArray = {
  id: string
  tabLabel?: string
  tabIcon?: React.ReactNode
  hasNotification?: Boolean
  notifTabIcon?: React.ReactNode
  data?: React.ReactNode
  windowTitle?: string | React.ReactNode
  width?: string
  discussionPanelClassName?: string
  disableSidebar?: boolean
  enableTab?: boolean
  enableInEntryCreatePage?: boolean
  isIndependentlyActive?: boolean
  onClick: () => void
  tabTestId?: string
  className?: string
  multipleTabs?: boolean;
  subTabsInfo?: subTabsArray[];
  waitOnClick?: () => Promise<any>
}

interface SideBarWindowProps {
  /**
   * An array of tab info objects(id, tabLabel, tabIcon, data)
   */
  tabsInfo?: Array<IArray>
  windowData?: {
    tabLabel?: string
    tabIcon?: React.ReactNode
    data?: React.ReactNode
    windowTitle?: string | React.ReactNode
    tabTestId?: string
  }
  isInsideEntryCreatePage?: boolean
  onSelect?: (data?: any) => void
  onClose?: (data?: any) => void
  borderOpen?: boolean
  borderClose?: boolean
  isResizable?: boolean
  defaultSelectedIndex?: number
  defaultSelectedId?: string
  onKeyDownCallback?: Function
  onResize?: Function
  defaultSelectedSubTabId?: string
  defaultSelectedSubTabIndex?: number
  /**
   * A boolean indicating whether the sidebar content is visible.
   * - `true`: The sidebar content is displayed.
   * - `false`: The sidebar content is hidden.
   * This prop is controlled by the parent component, allowing the parent to manage the visibility of the sidebar.
   */
  isSidebarContentVisible?: boolean

}
interface WindowContentProps {
  data?: React.ReactNode
  windowTitle?: string | React.ReactNode
  width?: string
  discussionPanelClassName?: string
  isResizable?: boolean
  onResize?: Function,
  activeTabId: number
  isSubTab?: boolean
}

export const getSelectedTabIndex = (tabsInfo: Array<any>, tabId?: string, tabIndex?: number) => {
  if (tabId) {
    const selectedTabIndex = tabsInfo?.findIndex(eachTab => eachTab.id === tabId);
    return selectedTabIndex >= 0 ? selectedTabIndex : null;
  }
  if (Number.isInteger(tabIndex) && tabIndex < tabsInfo?.length) {
    return tabIndex;
  }
  return null;
}

export const GetWindowContent = (props: WindowContentProps) => {
  const { data, windowTitle, width = '400px', discussionPanelClassName, isResizable, onResize, activeTabId } = props
  const oldResizeWidthRef = useRef(parseInt(width, 10));

  const rsMouseDownHandler = (e) => {
    e.preventDefault();
    const widthInNumber = parseInt(width, 10)
    const x = e.clientX;
    const panel = document.getElementById('resizable__panel')
    const initialWidth = oldResizeWidthRef.current;
    let newWidth = oldResizeWidthRef.current;

    const bottomLayer = document.getElementById("SidebarWindow-bottom-layer")
    if (bottomLayer) bottomLayer.style.display = "block";

    const mouseUpHandler = (e) => {
      e.preventDefault();
      const bottomLayer = document.getElementById("SidebarWindow-bottom-layer")
      if (bottomLayer) bottomLayer.style.display = "none";
      document.removeEventListener('mouseup', mouseUpHandler);
      document.removeEventListener('mousemove', mouseMoveHandler);
      const currentWidth = newWidth > 640 ? 640 : (newWidth < widthInNumber ? widthInNumber : newWidth);
      onResize && onResize({ width: initialWidth, currentWidth })
      oldResizeWidthRef.current = currentWidth;
    };

    const mouseMoveHandler = (e) => {
      e.preventDefault();
      const dx = x - e.clientX; // Resize from left to right
      const bottomLayer = document.getElementById("SidebarWindow-bottom-layer")
      if (bottomLayer) bottomLayer.style.display = "block";
      newWidth = initialWidth + dx;


      if (newWidth <= 640 && newWidth >= widthInNumber) panel.style.width = newWidth + 'px';
      else if (newWidth > 640) panel.style.width = '640px';
      else if (newWidth < widthInNumber) panel.style.width = widthInNumber + 'px';

      if (newWidth <= 400) {
        panel.classList.add('resizable__panel--min')
      } else {
        panel.classList.remove('resizable__panel--min')
      }
    };


    document.addEventListener('mousemove', mouseMoveHandler);
    document.addEventListener('mouseup', mouseUpHandler);
  };


  useEffect(() => {
    if (isResizable) {
      const panel = document.getElementById('resizable__panel')
      panel.style.width = width;
      document.getElementById("sidebar-resize-bar")?.addEventListener('mousedown', rsMouseDownHandler);

      return () => {
        const resizeBar = document.getElementById("sidebar-resize-bar");
        if (resizeBar) {
          resizeBar.removeEventListener('mousedown', rsMouseDownHandler);
        }
      };
    }


  }, [isResizable])

  if (data) {
    let style: any = { width: isResizable ? 'calc(100% - 72px)' : width }

    return (
      <div className={['SidebarWindow__content', discussionPanelClassName].join(' ')} style={style}>
        <div className="SidebarWindow__content__title">{windowTitle}</div>
        <div className="SidebarWindow__content__body">{data}</div>
      </div>
    )
  }

  return <p>No data defined for the tab</p>
}

const bottomLayerStyle: any = {
  position: "absolute",
  width: window.innerWidth,
  height: "100%",
  left: `-${window.innerWidth - 72}px`,
  display: "none",
  zIndex: 1,
  cursor: "col-resize",
};

const SideBarWindow = (props: SideBarWindowProps) => {
  const activeTabIndex = getSelectedTabIndex(props?.tabsInfo, props?.defaultSelectedId, props?.defaultSelectedIndex)
  const [isComponentVisible, setIsComponentVisible] = useState(Number.isInteger(activeTabIndex) && activeTabIndex < props?.tabsInfo?.length)
  const [selectedTabIndex, setSelectedTabIndex] = useState(
    Number.isInteger(activeTabIndex) && activeTabIndex < props?.tabsInfo?.length ? activeTabIndex : null
  )
  const activeSubTabIndex = getSelectedTabIndex(props.tabsInfo[selectedTabIndex]?.subTabsInfo, props?.defaultSelectedSubTabId, props?.defaultSelectedSubTabIndex)
  const [selectedSubTabIndex, setSelectedSubTabIndex] = useState(
    Number.isInteger(activeSubTabIndex) && activeSubTabIndex < props.tabsInfo[selectedTabIndex]?.subTabsInfo?.length ? activeSubTabIndex : null
  )

  const { tabsInfo = [], windowData, isInsideEntryCreatePage = false, onSelect, onClose, isResizable, onKeyDownCallback = () => { } } = props
  const [refresh, setRefresh] = useState(false) // this is an arbitary value just to reload component



  const initializeTabs = () => {
    const activeTabIndex = getSelectedTabIndex(
      props.tabsInfo,
      props?.defaultSelectedId,
      props?.defaultSelectedIndex
    );
    const activeSubTabIndex = getSelectedTabIndex(
      props.tabsInfo[activeTabIndex]?.subTabsInfo,
      props?.defaultSelectedSubTabId,
      props?.defaultSelectedSubTabIndex
    );

    const selectedTabIndex = Number.isInteger(activeTabIndex) && activeTabIndex < props.tabsInfo?.length ? activeTabIndex : null;
    const selectedSubTabIndex = Number.isInteger(activeSubTabIndex) && activeSubTabIndex < props.tabsInfo[activeTabIndex].subTabsInfo?.length ? activeSubTabIndex : null;

    setIsComponentVisible(Number.isInteger(activeTabIndex) && activeTabIndex < props.tabsInfo?.length);
    setSelectedTabIndex(selectedTabIndex);
    setSelectedSubTabIndex(selectedSubTabIndex);
  };

  React.useEffect(() => {
    initializeTabs();
  }, [props.tabsInfo[activeTabIndex]?.subTabsInfo?.length, props.tabsInfo.length])


  const selectTab = (index: number) => {
    if (!tabsInfo[index].hasOwnProperty('disableSidebar') && !tabsInfo[index].disableSidebar) {
      if (tabsInfo[index].hasOwnProperty('enableTab') && tabsInfo[index].enableTab === true) {
        if (tabsInfo[index].hasOwnProperty('onClick')) {
          tabsInfo[index].onClick()
          tabsInfo[index].isIndependentlyActive = !tabsInfo[index].isIndependentlyActive
          setRefresh((prevValue) => !prevValue)
        }
      } else if (!isInsideEntryCreatePage) {
        if (tabsInfo[index].hasOwnProperty('onClick')) {
          tabsInfo[index].onClick()
        }
        if (index === selectedTabIndex && isComponentVisible) {
          setIsComponentVisible(false)
          onClose && onClose()
          return
        }

        if (tabsInfo[index].hasOwnProperty('waitOnClick')) {
          tabsInfo[index].waitOnClick()
            .then(response => {
              if (response === true) {
                setSelectedTabIndex(index);
                setIsComponentVisible(true);
                if (onSelect) {
                  onSelect();
                }
              }
            })
            .catch(error => {
              console.error('Error during waitOnClick:', error);
            });
          return;
        }
        setIsComponentVisible(true)
        setSelectedTabIndex(index)
        if (onSelect) {
          onSelect()
        }
      }
    }
  }

  const selectSubTab = (parentTabIndex: number, subTabIndex: number) => {
    if (parentTabIndex >= 0 && parentTabIndex < tabsInfo.length) {
      const parentTab = tabsInfo[parentTabIndex];
      if (parentTab && parentTab.subTabsInfo && subTabIndex >= 0 && subTabIndex < parentTab?.subTabsInfo?.length) {
        const subTab = parentTab?.subTabsInfo[subTabIndex];
        if (!subTab.hasOwnProperty('disableSidebar') && !subTab.disableSidebar) {
          if (subTab.hasOwnProperty('onClick')) {
            subTab.onClick();
          }
          if (subTabIndex === selectedSubTabIndex && isComponentVisible) {
            setIsComponentVisible(false);
            onClose && onClose();
            return;
          }
          setIsComponentVisible(true);
          setSelectedTabIndex(parentTabIndex);
          setSelectedSubTabIndex(subTabIndex);
          if (onSelect) {
            onSelect();
          }
        }
      }
    }
  };

  const onCloseWindow = () => {
    if (!isInsideEntryCreatePage) {
      setIsComponentVisible(false)
      setSelectedTabIndex(null)
      if (onClose) onClose()
    }
  }

  const onKeyPressTabs = (e: any, index: number) => {
    if (e.key === "Enter" || e.key === " ") {
      selectTab(index)
    }
    props.onKeyDownCallback && props.onKeyDownCallback(e, (index === tabsInfo.length - 1))
  }

  useEffect(() => {
    if (typeof props.isSidebarContentVisible == "boolean") {
      if (props.isSidebarContentVisible) {
        // Set the sidebar as visible if `selectedTabIndex` exists and is valid.
        setIsComponentVisible(Number.isInteger(selectedTabIndex) && selectedTabIndex < props?.tabsInfo?.length);
      } else {
        // Close the sidebar window if `isSidebarContentVisible` is false.
        onCloseWindow();
      }
    }
  }, [props.isSidebarContentVisible]);

  return (
    <div
      data-test-id={props.windowData?.tabTestId ? `${props.windowData?.tabTestId}-window` : 'cs-sidebar-window'}
      className={`SidebarWindow flex ${isComponentVisible ? 'SidebarWindow--active' : ''}`} >
      {isResizable && <div id="SidebarWindow-bottom-layer" style={bottomLayerStyle} />}
      {props.hasOwnProperty('windowData') ? (
        <>
          {isComponentVisible && (
            <div>
              <div onClick={() => onCloseWindow()} className="RightSideHide SidebarWindow__hide-icon-container">
                <Icon icon="CaretRight" size="tiny" version='v2' />
              </div>
              <GetWindowContent data={windowData.data} windowTitle={windowData.windowTitle} onResize={props.onResize} activeTabId={activeTabIndex} />
            </div>
          )}
        </>
      ) : (
        <>
          {isComponentVisible && (
            <div id={isResizable ? 'resizable__panel' : ''}>
              <div className="resizable__panel__stroke" id={isResizable ? "sidebar-resize-bar" : undefined}></div>
              <div onClick={() => onCloseWindow()} className="RightSideHide SidebarWindow__hide-icon-container"><Icon icon="CaretRight" size="tiny" version='v2' /></div>

              {/* Conditional rendering based on multipleTabs */}
              {tabsInfo[selectedTabIndex]?.multipleTabs ? (
                // Render content based on selected subtab
                <GetWindowContent
                  data={tabsInfo[selectedTabIndex].subTabsInfo[selectedSubTabIndex].data}
                  windowTitle={tabsInfo[selectedTabIndex].subTabsInfo[selectedSubTabIndex].windowTitle}
                  width={tabsInfo[selectedTabIndex].subTabsInfo[selectedSubTabIndex].width}
                  discussionPanelClassName={tabsInfo[selectedTabIndex].subTabsInfo[selectedSubTabIndex].discussionPanelClassName}
                  isResizable={isResizable}
                  onResize={props.onResize}
                  activeTabId={activeTabIndex}
                />
              ) : (
                // Render content for single tab
                <GetWindowContent
                  data={tabsInfo[selectedTabIndex].data}
                  windowTitle={tabsInfo[selectedTabIndex].windowTitle}
                  width={tabsInfo[selectedTabIndex].width}
                  discussionPanelClassName={tabsInfo[selectedTabIndex].discussionPanelClassName}
                  isResizable={isResizable}
                  activeTabId={activeTabIndex}
                  onResize={props.onResize}

                />
              )}
            </div>
          )}
        </>
      )}

      <div
        className={`SidebarWindow__tabs-container
      ${!isComponentVisible && props.borderClose ? 'SidebarWindow__tabs-container--border' : ''}
      ${isComponentVisible && props.borderOpen ? 'SidebarWindow__tabs-container--border' : ''}`}>
        {props.hasOwnProperty('windowData') ? (
          <>
            {windowData && (
              <Tooltip
                testId={windowData.tabTestId}
                content={windowData.tabLabel}
                position="left"
                showArrow={false}
                interactive={false}
                popperOptions={{
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, -3]
                      }
                    },
                  ]
                }}
              >
                <div tabIndex={0} onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setIsComponentVisible(!isComponentVisible)
                  }
                }} className="SidebarWindowSingle__tab-item">
                  <div className="SidebarWindow__tab-item" onClick={() => setIsComponentVisible(!isComponentVisible)}>
                    {windowData.tabIcon}
                  </div>
                </div>
              </Tooltip>
            )}
          </>
        ) : (
          <>
            {tabsInfo &&
              tabsInfo.map((tab, index) => {
                if (tab.multipleTabs) {
                  const maxVisibleSubtabs = tabsInfo[index].subTabsInfo.length > 3 ? 2 : 3; // Maximum number of subtabs to render directly
                  // Render subtabs
                  const visibleSubtabs = tabsInfo[index].subTabsInfo.slice(0, maxVisibleSubtabs);
                  const hiddenSubtabs = tabsInfo[index].subTabsInfo.slice(maxVisibleSubtabs);
                  const listForDropdown = hiddenSubtabs.map(subTab => ({
                    ...subTab,
                    label: (
                      <div className='flex flex-v-center col-gap-15'>
                        {subTab.tabIcon && subTab.tabIcon}
                        {subTab.tabLabel}
                      </div>
                    )
                  }));

                  const handleSubTabChange = (e, index) => {

                    const subTabsInfo = tabsInfo[index]?.subTabsInfo;
                    const subIndex = subTabsInfo.findIndex(subTab => subTab.id === e.id);
                    return subIndex;
                  };


                  return (
                    <div className='multiple-subtabs flex-column'>
                      {/* Render visible subtabs */}
                      {visibleSubtabs.map((subTab, subIndex) => {
                        const subTabClassName = cn('SidebarWindow__subtab-item', {
                          'SidebarWindow__subtab-item--selected': selectedTabIndex === index && selectedSubTabIndex === subIndex,
                          'SidebarWindow__subtab-item--disabled':
                            (!subTab.enableInEntryCreatePage && isInsideEntryCreatePage) ||
                            (subTab.hasOwnProperty('disableSidebar') && subTab.disableSidebar),
                          'SidebarWindow__subtab-item--independently-active': subTab.isIndependentlyActive,
                        });

                        return (
                          <Tooltip
                            key={subIndex}
                            testId={subTab.tabTestId}
                            content={subTab.tabLabel}
                            position="left"
                            showArrow={false}
                            interactive={false}
                            popperOptions={{
                              modifiers: [
                                {
                                  name: 'offset',
                                  options: {
                                    offset: [0, -10],
                                  },
                                },
                              ],
                            }}
                          >
                            {tab.hasNotification ? (
                              selectedTabIndex === index && isComponentVisible ? (
                                <div tabIndex={0} onKeyDown={(e: any) => onKeyPressTabs(e, index)} className={subTabClassName} onClick={() => selectSubTab(index, subIndex)}>
                                  <div className='tab-iconWrapper'>
                                    {tab.tabIcon}
                                  </div>
                                </div>
                              ) : (
                                <div tabIndex={0} onKeyDown={(e: any) => onKeyPressTabs(e, index)} className={subTabClassName} onClick={() => selectSubTab(index, subIndex)}>
                                  <div className='tab-iconWrapper'>{tab.notifTabIcon}</div>
                                </div>
                              )
                            ) : (
                              <div tabIndex={0} onKeyDown={(e: any) => onKeyPressTabs(e, index)} className={subTabClassName} onClick={() => selectSubTab(index, subIndex)}>
                                <div className='tab-iconWrapper'>{subTab.tabIcon}</div>
                              </div>
                            )}
                          </Tooltip>
                        );

                      })}

                      {/* Render dropdown for hidden subtabs */}
                      {hiddenSubtabs.length > 0 && (
                        <Tooltip
                          key={index}
                          testId={'subtabs_dropdown'}
                          content={'More Apps'}
                          position="left"
                          showArrow={false}
                          interactive={false}
                          popperOptions={{
                            modifiers: [
                              {
                                name: 'offset',
                                options: {
                                  offset: [0, -10],
                                },
                              },
                            ],
                          }}
                        >
                          <div className="subtabs_dropdown ">
                            <Dropdown
                              version='v2'
                              list={listForDropdown}
                              type="click"
                              ellipseAlignment="left"
                              isDisabled={false}
                              onChange={(e) => {
                                selectSubTab(index, handleSubTabChange(e, index));
                              }}
                              className='subtabsdropdown subtab_ellipses'
                            >
                              <Icon icon="AppDrawer" version='v2' size="small" />
                            </Dropdown>
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  );

                }

                else {
                  // Render single tab
                  const tabClassName = cn('SidebarWindow__tab-item', {
                    'SidebarWindow__tab-item--selected': selectedTabIndex === index,
                    'SidebarWindow__tab-item--disabled':
                      (!tab.enableInEntryCreatePage && isInsideEntryCreatePage) ||
                      (tabsInfo[index].hasOwnProperty('disableSidebar') && tabsInfo[index].disableSidebar),
                    'SidebarWindow__tab-item--independently-active': tab.isIndependentlyActive,
                    '': refresh && false
                  }, tab.className);

                  return (
                    <Tooltip
                      key={index}
                      testId={tab.tabTestId}
                      content={tab.tabLabel}
                      position="left"
                      showArrow={false}
                      interactive={false}
                      popperOptions={{
                        modifiers: [
                          {
                            name: 'offset',
                            options: {
                              offset: [0, -10],
                            },
                          },
                        ],
                      }}
                    >
                      {tab.hasNotification ? (
                        selectedTabIndex === index && isComponentVisible ? (
                          <div tabIndex={0} onKeyDown={(e: any) => onKeyPressTabs(e, index)} className={tabClassName} onClick={() => selectTab(index)}>
                            <div className='tab-iconWrapper'>
                              {tab.tabIcon}
                            </div>
                          </div>
                        ) : (
                          <div tabIndex={0} onKeyDown={(e: any) => onKeyPressTabs(e, index)} className={tabClassName} onClick={() => selectTab(index)}>
                            <div className='tab-iconWrapper'>{tab.notifTabIcon}</div>
                          </div>
                        )
                      ) : (
                        <div tabIndex={0} onKeyDown={(e: any) => onKeyPressTabs(e, index)} className={tabClassName} onClick={() => selectTab(index)}>
                          <div className='tab-iconWrapper'>{tab.tabIcon}</div>
                        </div>
                      )}
                    </Tooltip>
                  );
                }
              })
            }


          </>
        )}
      </div>
    </div>
  )
}

export default SideBarWindow